import {PermAction, RequireFeature} from '@services/helper/permission.helper'
import {RequireLineManager} from '@context/MyContext'
import {ComponentType, createElement, lazy} from 'react'

/**
 * Wrap a lazily-loaded page in a RequireFeature guard so a direct URL visit is
 * redirected to the landing router when the user lacks the feature. Kept JSX-free so
 * route files stay `.ts`. `anyOf` = feature URL(s) that grant access. `action` is what the
 * page needs on one of them: Show by default, 'create' for an add route, 'update' for an edit route.
 */
export const guard = (
  importer: () => Promise<{default: ComponentType<any>}>,
  anyOf: string[],
  action: PermAction = 'show'
) => {
  const Comp = lazy(importer)
  return () => createElement(RequireFeature, {anyOf, action, children: createElement(Comp)})
}

/**
 * Wrap a page so it needs BOTH a feature grant and ≥1 direct report.
 *
 * The grant answers "may this role use the page"; line-manager status is derived at
 * runtime from subordinateCount and cannot be granted. Team attendance needs both:
 * without the runtime check any granted employee could open a colleague-listing page
 * directly by URL, bypassing the menu.
 */
export const guardLineManagerFeature = (
  importer: () => Promise<{default: ComponentType<any>}>,
  anyOf: string[]
) => {
  const Comp = lazy(importer)
  return () =>
    createElement(RequireFeature, {
      anyOf,
      children: createElement(RequireLineManager, {children: createElement(Comp)}),
    })
}
